import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import FeatherIcon from "feather-icons-react";
import debounce from "lodash.debounce";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import useAxios from "../../utils/useAxios";
import AuthContext from "../../contexts/AuthContext";
import TournamentMenuList from "../../components/TournamentMenuList";
import { CustomModal } from "../../components/CustomModel/CustomModal";
import PhotoLightbox from "../../components/PhotoLightbox";
import SelectInput from "../../components/SelectInput";
import RatingTable from "../ratings/RatingTable";
import TournamentShareModal from "../tournaments/export/TournamentShareModal";
import { getInitials, sports, getGradeName, COUNTRY_OPTIONS, LEVEL_OPTIONS } from "../../constants";
import { getOptionName } from "../../utils/getOptionName";
import { useTheme } from "../../contexts/ThemeContext";
import { CompactTableRow } from "../../components/CompactTableRow";
import { parseDate } from "../../utils/parseDate";

// start_date/end_date carry an actual time of day (same reasoning as
// TournamentsList.js's own copy of this) - compares real timestamps with
// no clamping to day boundaries, so a same-day 18:00-21:00 tournament isn't
// "live" all day just because it's the right calendar day.
const getTournamentStatus = (tournament) => {
    if (!tournament.start_date || !tournament.end_date) return "upcoming";
    const now = new Date();
    const start = parseDate(tournament.start_date);
    if (!start) return "upcoming";
    const end = parseDate(tournament.end_date);
    if (!end) return "upcoming";
    if (now < start) return "upcoming";
    if (now > end) return "completed";
    return "live";
};

const isSameCalendarDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// Same palette/rules as TournamentsList.js's own getStatusClass, so a
// club's calendar reads consistently with the main tournaments list.
const getStatusClass = (tournament) => {
    const status = getTournamentStatus(tournament);
    if (status === "live") return "in-progress";
    if (status === "completed") return "completed";
    const start = parseDate(tournament.start_date);
    if (start && isSameCalendarDay(start, new Date())) return "today";
    return "expected";
};

const formatYMD = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const formatTitle = (text) => {
    if (!text) return "";
    return text.toLowerCase().split(" ").map(word => word.split("-").map(part => part.charAt(0).toUpperCase() + part.slice(1)).join("-")).join(" ");
};

const tournamentsCountLabel = (count, t) => {
    const mod10 = count % 10;
    const mod100 = count % 100;
    let key = "tournament_many";
    if (mod10 === 1 && mod100 !== 11) key = "tournament_one";
    else if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) key = "tournament_few";
    return `${count} ${t(key)}`;
};

const clubCache = {};
const clubTournamentsCache = {};
const clubPlayersCache = {};

const ClubCalendarTab = ({ clubId, searchInputRef, searchOpen }) => {
    const { t, i18n } = useTranslation();
    const api = useAxios();
    const history = useHistory();
    const { compactTable } = useTheme();
    // This modal has no admin/public route split of its own (unlike
    // TournamentsList.js's /admin/tournaments vs /tournaments) - it's the
    // same component regardless of where it's opened from. Logged-in-at-all
    // is the same coarse gate AuthorizedRoute.js itself uses elsewhere
    // (TournamentMenuList's own delete call still 403s server-side for
    // someone logged in but not actually allowed to touch this tournament).
    const { user } = useContext(AuthContext);
    const isAuthorized = !!user;
    const [year, setYear] = useState(() => new Date().getFullYear());
    const cacheKey = `${clubId}_${year}`;
    const [tournaments, setTournaments] = useState(() => clubTournamentsCache[cacheKey] || []);
    const [currentPage, setCurrentPage] = useState("");
    const [loader, setLoader] = useState(false);
    const [skeletonCards, setSkeletonCards] = useState(() => (clubTournamentsCache[cacheKey]?.length > 0 ? 0 : 5));
    const [searchQuery, setSearchQuery] = useState("");
    const { ref, inView } = useInView({ threshold: 0, rootMargin: '200px' });
    const requestIdRef = useRef(0);

    // Mirrors Tournament.js's own toggleSearch - closing the search (icon
    // click, or a tab switch while it's open, see ClubProfileModal's own
    // activeTab effect) clears the query too, so a hidden box can't keep
    // silently filtering the list with no visible reason why.
    useEffect(() => {
        if (!searchOpen) setSearchQuery("");
    }, [searchOpen]);

    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const options = [];
        for (let y = currentYear + 2; y >= currentYear - 6; y--) options.push({ id: y, name: String(y) });
        return options;
    }, []);

    useEffect(() => {
        requestIdRef.current += 1;
        const firstDay = new Date(year, 0, 1);
        const lastDay = new Date(year, 11, 31);
        const params = new URLSearchParams();
        params.set("club", clubId);
        params.set("all", "true");
        params.set("start_date", formatYMD(firstDay));
        params.set("end_date", formatYMD(lastDay));

        const cached = clubTournamentsCache[`${clubId}_${year}`];
        if (cached && cached.length > 0) {
            setTournaments(cached);
            setSkeletonCards(0);
        } else {
            setTournaments([]);
            setSkeletonCards(5);
        }
        setCurrentPage(`/api/v1.0/tournaments/search/?${params.toString()}`);
    }, [clubId, year]);

    const handleDeleteTournament = (id) => {
        setTournaments(prev => prev.filter(t => t.id !== id));
    };

    const getTournaments = async () => {
        const requestId = requestIdRef.current;
        setLoader(true);
        try {
            const response = await api.get(currentPage);
            if (requestId !== requestIdRef.current) return;

            const results = response.data.results || [];
            const totalCount = response.data.count || 0;

            setTournaments(prev => {
                const existingIds = new Set(prev.map(t => t.id));
                const newItems = results.filter(t => !existingIds.has(t.id));
                const updated = [...prev, ...newItems];
                clubTournamentsCache[`${clubId}_${year}`] = updated;
                return updated;
            });
            setCurrentPage(response.data.next ? response.data.next.replace(/^https?:\/\/[^/]+/, "") : null);

            const updatedCount = tournaments.length + results.length;
            const remaining = totalCount - updatedCount;
            setSkeletonCards(remaining > 0 ? Math.min(5, remaining) : 0);
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            console.error(error);
        } finally {
            if (requestId === requestIdRef.current) setLoader(false);
        }
    };

    useEffect(() => {
        if ((inView || tournaments.length === 0) && currentPage && !loader) {
            getTournaments();
        }
    }, [inView, currentPage, loader]);

    const currentLang = (typeof i18n !== "undefined" && i18n.language) ? i18n.language : "uk";

    // newest month on top, older ones lazy-load in and append at the bottom
    // as the sentinel below the list scrolls into view (same infinite-scroll
    // shape as the main tournaments list) - independent of whatever order
    // each fetched page happens to arrive in.
    const monthGroups = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        const sorted = [...tournaments]
            .filter(tournament => tournament.start_date)
            .filter(tournament => !query || tournament.name?.toLowerCase().includes(query))
            .sort((a, b) => {
                const dateA = parseDate(a.start_date) || 0;
                const dateB = parseDate(b.start_date) || 0;
                return dateB - dateA;
            });

        const groups = [];
        sorted.forEach(tournament => {
            const d = parseDate(tournament.start_date);
            if (!d) return;
            const key = `${d.getFullYear()}-${d.getMonth()}`;
            const last = groups[groups.length - 1];
            if (last && last.key === key) {
                last.items.push(tournament);
                return;
            }
            groups.push({ key, label: formatTitle(d.toLocaleString(currentLang, { month: "long" })), items: [tournament] });
        });
        return groups;
    }, [tournaments, currentLang, searchQuery]);

    // Running index over just the data rows (not the month dividers
    // interleaved between them) - same reasoning as TournamentsList.js's own
    // compactRowIndex, a plain nth-child would miscount past the dividers.
    let compactRowIndex = 0;

    return (
        <div>
            <div style={{ margin: "10px 0" }}>
                <div style={{ width: "140px" }}>
                    <SelectInput
                        data={yearOptions}
                        default_text={String(year)}
                        default_value={year}
                        setInputValue={setYear}
                        input_name="year_select"
                        show_label={false}
                        background={true}
                    />
                </div>
                {searchOpen && (
                    <div className="form-group" style={{ position: "relative", marginTop: "10px" }}>
                        <FeatherIcon icon="search" size={16} className="search-icon" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder={t("Search tournament by name")}
                            style={{ paddingLeft: "30px" }}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search"
                        />
                        {searchQuery && (
                            <FeatherIcon
                                icon="x"
                                size={16}
                                onClick={() => setSearchQuery("")}
                                style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "#888", cursor: "pointer" }}
                            />
                        )}
                    </div>
                )}
            </div>

            {!loader && tournaments.length === 0 && currentPage === null ? (
                <p style={{ padding: "15px", textAlign: "center", color: "var(--secondary-color)" }}>{t("No tournaments this year")}</p>
            ) : !loader && monthGroups.length === 0 && currentPage === null ? (
                <p style={{ padding: "15px", textAlign: "center", color: "var(--secondary-color)" }}>{t("No tournaments found")}</p>
            ) : compactTable ? (
                <div className="trows">
                    {monthGroups.map(group => (
                        <React.Fragment key={group.key}>
                            <div className="week-divider">
                                <span className="week-divider-range">{group.label} {year}</span>
                                <span className="week-divider-count">{tournamentsCountLabel(group.items.length, t)}</span>
                            </div>
                            {group.items.map(tournament => {
                                const locationValue = [tournament.city, tournament.location].filter(Boolean).join(", ");
                                const playersValue = tournament.players != null
                                    ? `${tournament.players}${tournament.player_quantity != null ? `/${tournament.player_quantity}` : ""}`
                                    : "—";
                                const zebra = compactRowIndex++ % 2 === 1;
                                return (
                                    <CompactTableRow
                                        key={tournament.id}
                                        variant="tournaments"
                                        zebra={zebra}
                                        statusDotClass={getStatusClass(tournament)}
                                        avatarSrc={tournament.club_logo || tournament.thumbnail || tournament.image || tournament.location_image}
                                        title={formatTitle(tournament.name)}
                                        onClick={() => history.push(`/tournaments/${tournament.id}`)}
                                        stats={[
                                            { icon: "calendar", value: tournament.date_range || "—" },
                                            { icon: "map-pin", value: locationValue || "—", className: "stat-location" },
                                            { icon: "users", value: playersValue }
                                        ]}
                                        actions={isAuthorized && (
                                            <TournamentMenuList tournamentId={tournament.id} onDeleted={handleDeleteTournament} asModal title={formatTitle(tournament.name)} />
                                        )}
                                    />
                                );
                            })}
                        </React.Fragment>
                    ))}
                    {(loader || (currentPage && tournaments.length === 0)) && skeletonCards > 0 && Array.from({ length: skeletonCards }).map((_, idx) => (
                        <div className="trow trow-tournaments" key={`skel-trow-${idx}`} style={{ pointerEvents: "none" }}>
                            <span />
                            <Skeleton circle width={20} height={20} />
                            <Skeleton width="60%" height={12} />
                            <Skeleton width="90%" height={11} />
                            <Skeleton width="90%" height={11} />
                            <Skeleton width="90%" height={11} />
                            <span />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="tournament-cards">
                    {monthGroups.map(group => (
                        <React.Fragment key={group.key}>
                            <div className="week-divider">
                                <span className="week-divider-range">{group.label} {year}</span>
                                <span className="week-divider-count">{tournamentsCountLabel(group.items.length, t)}</span>
                            </div>
                            {group.items.map(tournament => {
                                const status = getTournamentStatus(tournament);
                                // Badge inside the expanded details only ever shows one of
                                // these three (no separate "starts today" wording there) -
                                // the pill above it is the one that also gets the "today"
                                // highlight, via getStatusClass.
                                const statusClass = status === "live" ? "in-progress" : status === "completed" ? "completed" : "expected";
                                const statusIcon = statusClass === "in-progress" ? "activity" : statusClass === "completed" ? "check" : "clock";
                                const statusLabel = status === "live" ? t("In progress") : status === "completed" ? t("Completed") : t("Expected");
                                return (
                                    <div
                                        className="tournament-card"
                                        key={tournament.id}
                                        onClick={() => history.push(`/tournaments/${tournament.id}`)}
                                    >
                                        <div className={`tournament-card-date ${getStatusClass(tournament)}`}>{tournament.date_range}</div>
                                        {isAuthorized && (
                                            <div className="tournament-card-actions" onClick={e => e.stopPropagation()}>
                                                <TournamentMenuList tournamentId={tournament.id} onDeleted={handleDeleteTournament} asModal title={formatTitle(tournament.name)} />
                                            </div>
                                        )}
                                        <div className="tournament-card-top">
                                            <div className="tournament-card-thumb">
                                                {(() => {
                                                    const thumbSrc = tournament.club_logo || tournament.thumbnail || tournament.image || tournament.location_image;
                                                    if (thumbSrc) return <img src={thumbSrc} alt="" />;
                                                    const label = tournament.club || tournament.name;
                                                    return label
                                                        ? <span style={{ fontSize: "16px", fontWeight: 600 }}>{getInitials(label)}</span>
                                                        : <FeatherIcon icon="camera-off" />;
                                                })()}
                                            </div>
                                            <div className="tournament-card-titles">
                                                <div className="tournament-card-title">{formatTitle(tournament.name)}</div>
                                                <div className="tournament-card-club">{tournament.club}</div>
                                                <div className="tournament-card-info">
                                                    {tournament.city}
                                                    {tournament.city && tournament.players != null && " · "}
                                                    {tournament.players != null && (
                                                        <span className="tournament-card-players">
                                                            <FeatherIcon icon="users" size={11} />{tournament.players}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <details className="tournament-card-details">
                                            <summary className="tournament-card-toggle" onClick={e => e.stopPropagation()}>
                                                <svg className="tournament-card-toggle-arrow" height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                                    <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
                                                </svg>
                                            </summary>
                                            <div className="tournament-card-meta">
                                                <table className="tournament_table">
                                                    <tbody>
                                                    <tr>
                                                        <td>{t("Status")}</td>
                                                        <td>
                                                            <span className={`t-status-badge-sm inline ${statusClass}`}>
                                                                <FeatherIcon icon={statusIcon} />{statusLabel}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr><td>{t("Date")}</td><td>{tournament.days?.length > 0 ? tournament.days.map(day => t(`days_short.${day}`)).join("-") + " · " : ""}{tournament.date_range}</td></tr>
                                                    <tr><td>{t("Location")}</td><td>{tournament.location} · {tournament.city}{tournament.city && tournament.country && ", "}{t(getOptionName(COUNTRY_OPTIONS, tournament.country))}</td></tr>
                                                    <tr><td>{t("Format")}</td><td>{getGradeName(tournament.grade)} ·&nbsp;{tournament.gender && ((tournament.gender === 3 && tournament.grade === 1) ? t("Admixed") : t(`gender.${tournament.gender}`))}</td></tr>
                                                    <tr><td>{t("Level")}</td><td>{tournament.format}</td></tr>
                                                    <tr><td>{t("Players")}</td><td style={{ textAlign: "right" }}><div style={{ position: "relative", height: "30px" }}><div className="tournament-players" style={{ color: "var(--primary-color) !important", position: "absolute", right: 0, top: "-5px" }}>{tournament.players}</div></div></td></tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </details>
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    ))}
                    {(loader || (currentPage && tournaments.length === 0)) && skeletonCards > 0 && Array.from({ length: skeletonCards }).map((_, idx) => (
                        <div className="tournament-card" key={`skel-${idx}`} style={{ pointerEvents: 'none' }}>
                            <div className="tournament-card-date" style={{ border: 'none', background: 'transparent', padding: 0 }}>
                                <Skeleton width={56} height={16} borderRadius={20} />
                            </div>
                            <div className="tournament-card-top">
                                <div className="tournament-card-thumb" style={{ background: 'transparent' }}>
                                    <Skeleton circle width={52} height={52} />
                                </div>
                                <div className="tournament-card-titles" style={{ flex: 1, minWidth: 0 }}>
                                    <div className="tournament-card-title"><Skeleton width="65%" height={16} /></div>
                                    <div className="tournament-card-club"><Skeleton width="40%" height={13} style={{ marginTop: 4 }} /></div>
                                    <div className="tournament-card-info"><Skeleton width="50%" height={11} style={{ marginTop: 4 }} /></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <div ref={ref} style={{ height: "20px", width: "100%" }}></div>
        </div>
    );
};

const ClubRatingsTab = ({ clubId }) => {
    const { t } = useTranslation();
    const api = useAxios();
    const { compactTable } = useTheme();
    const [ratings, setRatings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openRatingId, setOpenRatingId] = useState(null);

    useEffect(() => {
        setLoading(true);
        api.get(`/api/v1.0/ratings/?club=${clubId}&club_id=${clubId}&limit=100`)
            .then(res => {
                const list = res.data?.results || (Array.isArray(res.data) ? res.data : []);
                const filtered = list.filter(rating => {
                    const c = rating.club?.id || rating.club || rating.club_id;
                    return String(c).toLowerCase() === String(clubId).toLowerCase();
                });
                setRatings(filtered);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [clubId]);

    if (loading) {
        return compactTable ? (
            <div className="trows">
                {Array.from({ length: 3 }).map((_, idx) => (
                    <div className="trow trow-entity" key={idx} style={{ pointerEvents: "none" }}>
                        <Skeleton circle width={20} height={20} />
                        <Skeleton width="60%" height={12} />
                        <span />
                    </div>
                ))}
            </div>
        ) : (
            <div className="entity-cards" style={{ marginTop: "10px" }}>
                {Array.from({ length: 3 }).map((_, idx) => (
                    <Skeleton key={idx} height={90} baseColor="#fff" highlightColor="#f3f3f3" className="entity-card-skeleton" />
                ))}
            </div>
        );
    }
    if (ratings.length === 0) return <p style={{ padding: "15px", textAlign: "center", color: "var(--secondary-color)" }}>{t("No ratings yet")}</p>;

    return (
        <>
            {compactTable ? (
                <div className="trows">
                    {ratings.map((rating, idx) => (
                        <CompactTableRow
                            key={rating.id}
                            variant="entity"
                            zebra={idx % 2 === 1}
                            statusDotClass={getStatusClass(rating)}
                            title={rating.name}
                            subtitle={`${rating.start_date ? new Date(rating.start_date).toLocaleDateString("uk-UA") : "—"} – ${rating.end_date ? new Date(rating.end_date).toLocaleDateString("uk-UA") : "—"}`}
                            onClick={() => setOpenRatingId(rating.id)}
                        />
                    ))}
                </div>
            ) : (
            <div className="entity-cards" style={{ marginTop: "10px" }}>
                {ratings.map(rating => (
                    <div
                        className="entity-card rating-card"
                        key={rating.id}
                        onClick={() => setOpenRatingId(rating.id)}
                    >
                        <div className="entity-card-top">
                            <div className="entity-card-thumb">{getInitials(rating.name)}</div>
                            <div className="entity-card-titles">
                                <div className="entity-card-title">{rating.name}</div>
                            </div>
                        </div>
                        <div className={`entity-card-line ${getStatusClass(rating)}`}>
                            {rating.start_date ? new Date(rating.start_date).toLocaleDateString("uk-UA") : "—"} – {rating.end_date ? new Date(rating.end_date).toLocaleDateString("uk-UA") : "—"}
                        </div>
                    </div>
                ))}
            </div>
            )}
            <CustomModal isOpen={!!openRatingId} onClose={() => setOpenRatingId(null)} maxWidth="1200px" hideCloseButton>
                {openRatingId && <RatingTable ratingId={openRatingId} onClose={() => setOpenRatingId(null)} />}
            </CustomModal>
        </>
    );
};

const ClubPlayersTab = ({ clubId, searchInputRef, searchOpen }) => {
    const { t } = useTranslation();
    const api = useAxios();
    const history = useHistory();
    const location = useLocation();
    const { compactTable } = useTheme();
    const [players, setPlayers] = useState(() => clubPlayersCache[clubId]?.players || []);
    const [currentPage, setCurrentPage] = useState("");
    const [loader, setLoader] = useState(false);
    const [skeletonCards, setSkeletonCards] = useState(() => (clubPlayersCache[clubId]?.players?.length > 0 ? 0 : 5));
    const [failed, setFailed] = useState(false);
    // Two pieces of state for one box on purpose: `searchQuery` is what the
    // input shows (updates on every keystroke), `query` is what's actually
    // been sent to the backend (debounced). Searching server-side rather
    // than filtering in the browser is what makes this box find a player
    // who hasn't been scrolled to yet - a club this size runs into the
    // hundreds, and only a couple of pages of them are loaded at any time.
    const [searchQuery, setSearchQuery] = useState("");
    const [query, setQuery] = useState("");
    const { ref, inView } = useInView({ threshold: 0, rootMargin: '200px' });
    const requestIdRef = useRef(0);

    const debouncedSetQuery = useMemo(() => debounce(setQuery, 400), []);
    useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

    // Same reasoning as ClubCalendarTab's own identical effect - closing
    // the search clears both the shown value and the debounced one actually
    // sent to the backend, instead of leaving a hidden box still filtering.
    useEffect(() => {
        if (!searchOpen) {
            setSearchQuery("");
            debouncedSetQuery.cancel();
            setQuery("");
        }
    }, [searchOpen, debouncedSetQuery]);

    // Same viewport-derived first page as PlayersDirectory.js/ClubsList.js -
    // a ~40px compact row means the backend's default page size leaves
    // most of a tall screen empty, forcing extra scroll-triggered fetches
    // just to fill it. Card view keeps the backend default.
    const compactPageSize = () => Math.max(10, Math.ceil(window.innerHeight / 40) + 5);

    useEffect(() => {
        requestIdRef.current += 1;
        const params = new URLSearchParams();
        // Matches the backend's own threshold (ClubPlayersAPIView ignores a
        // `q` under 2 chars), so a single typed letter doesn't fire a
        // request that would come back as the unfiltered list anyway.
        if (query.trim().length >= 2) params.set("q", query.trim());
        if (compactTable) params.set("limit", String(compactPageSize()));

        // Only the unsearched list is cached (that's the cold open of this
        // tab, the one worth painting instantly on a revisit); a given
        // search query is a one-off and not worth keeping.
        const cached = query.trim().length >= 2 ? null : clubPlayersCache[clubId];
        setFailed(false);
        const search = params.toString();
        const firstPage = `/api/v1.0/clubs/${clubId}/players/${search ? `?${search}` : ""}`;
        if (cached && cached.players.length > 0) {
            // Resuming from the cached `next` rather than page 1 is the point
            // of storing it: re-walking the pages already in the cache would
            // be that many requests deduping to nothing before reaching any
            // player the list doesn't already have.
            setPlayers(cached.players);
            setSkeletonCards(0);
            setCurrentPage(cached.next);
        } else {
            setPlayers([]);
            setSkeletonCards(5);
            setCurrentPage(firstPage);
        }
    }, [clubId, query, compactTable]);

    const getPlayers = async () => {
        const requestId = requestIdRef.current;
        setLoader(true);
        try {
            const response = await api.get(currentPage);
            if (requestId !== requestIdRef.current) return;

            const results = response.data.results || [];
            const totalCount = response.data.count || 0;
            const next = response.data.next ? response.data.next.replace(/^https?:\/\/[^/]+/, "") : null;

            setPlayers(prev => {
                const existingIds = new Set(prev.map(p => p.id));
                const updated = [...prev, ...results.filter(p => !existingIds.has(p.id))];
                if (query.trim().length < 2) clubPlayersCache[clubId] = { players: updated, next };
                return updated;
            });
            setCurrentPage(next);

            const updatedCount = players.length + results.length;
            const remaining = totalCount - updatedCount;
            setSkeletonCards(remaining > 0 ? Math.min(5, remaining) : 0);
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            console.error(error);
            setFailed(true);
            // Clearing currentPage is what stops the effect below from
            // retrying forever: it re-runs whenever `loader` flips back to
            // false, and with an empty list its own `players.length === 0`
            // condition would otherwise still hold.
            setCurrentPage(null);
            setSkeletonCards(0);
        } finally {
            if (requestId === requestIdRef.current) setLoader(false);
        }
    };

    useEffect(() => {
        if ((inView || players.length === 0) && currentPage && !loader) {
            getPlayers();
        }
    }, [inView, currentPage, loader]);

    const handleItemClick = (player) => {
        const params = new URLSearchParams(location.search);
        params.set("player_id", player.id);
        history.push({ search: params.toString() });
    };

    const isSearching = query.trim().length >= 2;
    const showSkeletons = (loader || (currentPage && players.length === 0)) && skeletonCards > 0;

    return (
        <div>
            {searchOpen && (
                <div style={{ margin: "10px 0" }}>
                    <div className="form-group" style={{ position: "relative" }}>
                        <FeatherIcon icon="search" size={16} className="search-icon" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder={t("Search player by name")}
                            style={{ paddingLeft: "30px" }}
                            value={searchQuery}
                            onChange={(e) => { setSearchQuery(e.target.value); debouncedSetQuery(e.target.value); }}
                            className="search"
                        />
                        {searchQuery && (
                            <FeatherIcon
                                icon="x"
                                size={16}
                                onClick={() => { setSearchQuery(""); debouncedSetQuery.cancel(); setQuery(""); }}
                                style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "#888", cursor: "pointer" }}
                            />
                        )}
                    </div>
                </div>
            )}

            {failed && players.length === 0 ? (
                <p style={{ padding: "15px", textAlign: "center", color: "var(--secondary-color)" }}>{t("Something went wrong!")}</p>
            ) : !loader && players.length === 0 && currentPage === null ? (
                <p style={{ padding: "15px", textAlign: "center", color: "var(--secondary-color)" }}>{isSearching ? t("No players found") : t("No players")}</p>
            ) : compactTable ? (
                <div className="trows">
                    {players.map((player, idx) => (
                        <CompactTableRow
                            key={player.id || idx}
                            variant="entity"
                            zebra={idx % 2 === 1}
                            avatarSrc={player.image}
                            title={`${player.last_name} ${player.first_name}`.trim()}
                            subtitle={[
                                player.city,
                                player.country && t(getOptionName(COUNTRY_OPTIONS, player.country)),
                                player.level && t(getOptionName(LEVEL_OPTIONS, player.level))
                            ].filter(Boolean).join(", ")}
                            onClick={() => handleItemClick(player)}
                        />
                    ))}
                    {showSkeletons && Array.from({ length: skeletonCards }).map((_, idx) => (
                        <div className="trow trow-entity" key={`skel-player-${idx}`} style={{ pointerEvents: "none" }}>
                            <Skeleton circle width={20} height={20} />
                            <Skeleton width="60%" height={12} />
                            <span />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="entity-cards">
                    {players.map((player, idx) => (
                        <div className="entity-card" key={player.id || idx} onClick={() => handleItemClick(player)}>
                            <div className="entity-card-top">
                                <div className="entity-card-thumb">
                                    {player.image
                                        ? <img src={player.image} alt="" />
                                        : <FeatherIcon icon="user" />}
                                </div>
                                <div className="entity-card-titles">
                                    <div className="entity-card-title"><span className="uppercase">{player.last_name}</span> {player.first_name}</div>
                                    <div className="entity-card-subtitle">
                                        {player.city}
                                        {player.city && player.country && ", "}
                                        {t(getOptionName(COUNTRY_OPTIONS, player.country))}
                                        {(player.city || player.country) && player.level && ", "}
                                        {player.level && t(getOptionName(LEVEL_OPTIONS, player.level))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    {showSkeletons && Array.from({ length: skeletonCards }).map((_, idx) => (
                        <Skeleton key={`skel-player-${idx}`} height={82} baseColor="#fff" highlightColor="#f3f3f3" className="entity-card-skeleton" />
                    ))}
                </div>
            )}
            <div ref={ref} style={{ height: "20px", width: "100%" }}></div>
        </div>
    );
};

const ClubProfileModal = () => {
    const { t } = useTranslation();
    const api = useAxios();
    const history = useHistory();
    const location = useLocation();

    const query = new URLSearchParams(location.search);
    const clubId = query.get("club_id");

    const [club, setClub] = useState(() => (clubId ? clubCache[clubId] || null : null));
    const [loading, setLoading] = useState(() => (clubId ? !clubCache[clubId] : false));
    const [activeTab, setActiveTab] = useState("calendar");
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [lightboxSrc, setLightboxSrc] = useState(null);
    // Telegram-style profile: the hero and the compact header are two views
    // of the same scroll position, not two independently toggled states -
    // `progress` (0 = hero fully shown, 1 = fully collapsed) is read
    // straight off real scrollTop every scroll frame, and drives the
    // avatar's shrink and the compact row's fade-in continuously, so one
    // visibly morphs into the other as you scroll instead of one abruptly
    // disappearing while the other fades in on its own schedule.
    // px of scroll for a full hero <-> header morph - the hero's own real
    // rendered height (measured in the effect below), not a guessed
    // constant: guessing too short left a visible remnant of the hero
    // still poking out from under the sticky compact header even at
    // progress===1, since scrolling that far didn't actually clear it.
    const collapseRangeRef = useRef(90);
    const heroRef = useRef(null);
    // Starts at 0 (expanded hero) - opens into the full top block, not
    // straight into the compact header. The effect below pins real scroll
    // to match (0) on mount, so this initial value and the real scroll
    // position agree from frame one.
    const [progress, setProgress] = useState(0);

    // Which element actually scrolls isn't fixed: on desktop it's
    // .modal-overlay (overflow-y:auto), but CustomModal.css's own
    // @media(max-width:768px) block flips that to overflow-y:hidden and
    // makes .modal-content itself the scrollable one instead (so the
    // scrollbar sits inside the fixed-width box rather than shrinking it -
    // see that file's own comment). Both are found via closest() and
    // listened on - only one of them ever actually fires scroll events, so
    // this works under either CSS path without needing to detect which one
    // applies. Only runs once per fresh open (deps below), NOT per tab
    // switch - re-collapsing on every switch is itself the jump the user
    // doesn't want, even once it's not janky about it. Instead, the tab-
    // content wrapper's own fixed min-height keeps every tab tall enough to
    // hold whatever scroll position was already there, so switching never
    // needs to touch scrollTop at all - nothing to clamp, nothing to jump.
    useLayoutEffect(() => {
        let cancelled = false;
        let setupTimerId = null;
        let pinTimerId = null;
        let attachedTargets = [];
        let onScroll = null;

        // heroRef.current can still be null on the very first invocation of
        // this effect (observed on a hard/direct page load straight into an
        // already-open modal, i.e. clubId present from the first render -
        // the ref commit and this layout effect don't land in the same pass
        // there the way they normally do) - retries until the node (and at
        // least one scrollable ancestor) actually exists, instead of
        // silently giving up on a one-shot null check.
        //
        // setTimeout, not requestAnimationFrame: rAF callbacks are paused
        // entirely by the browser while the tab is backgrounded/hidden (a
        // real scenario - e.g. a club link opened in a background tab, not
        // just a testing quirk), which would leave this retry stuck
        // forever rather than just running a bit less smoothly. None of
        // these loops animate anything frame-by-frame - they're all "keep
        // checking a condition until it holds", so losing rAF's frame-sync
        // costs nothing here.
        const trySetup = () => {
            if (cancelled) return;
            const hero = heroRef.current;
            const overlay = hero?.closest(".modal-overlay");
            const content = hero?.closest(".modal-content");
            const targets = [overlay, content].filter(Boolean);
            if (!hero || targets.length === 0) {
                setupTimerId = setTimeout(trySetup, 16);
                return;
            }
            collapseRangeRef.current = hero.offsetHeight || collapseRangeRef.current;

            // Doesn't touch scrollTop until the container can actually hold
            // it: right when the node first exists, the tab's real content
            // (tournament list) often hasn't rendered its full height yet,
            // so an immediate assignment would clamp back to whatever it
            // can reach - and settling there, growing, then jumping to the
            // real target a frame later read as the header/avatar visibly
            // trembling rather than a single clean collapse.
            const waitUntilTall = () => {
                if (cancelled) return;
                const tallEnough = targets.some(t => t.scrollHeight - t.clientHeight >= collapseRangeRef.current - 1);
                if (!tallEnough) {
                    pinTimerId = setTimeout(waitUntilTall, 16);
                    return;
                }
                // Even now, CustomModal's own opening transition/focus
                // handling can still reset scroll once more right after -
                // reasserting for a window (not just once) outlasts that
                // without reintroducing the "too early" wobble above, since
                // this only starts once content is confirmed tall enough to
                // hold the value in the first place. ~1.5s, not a couple
                // hundred ms - a tab's own async data load (e.g. a club's
                // ratings fetch) swapping loading-skeleton content for the
                // real, differently-sized content is enough on its own to
                // trigger another reset, and that swap can land well after
                // a short window already gave up and stopped reasserting.
                let tick = 0;
                const PIN_TICKS = 90;
                const reassert = () => {
                    if (cancelled) return;
                    targets.forEach(t => { t.scrollTop = 0; });
                    tick += 1;
                    if (tick < PIN_TICKS) pinTimerId = setTimeout(reassert, 16);
                };
                reassert();
            };
            waitUntilTall();

            onScroll = () => {
                const scrollTop = Math.max(overlay?.scrollTop || 0, content?.scrollTop || 0);
                setProgress(Math.min(1, scrollTop / collapseRangeRef.current));
            };
            targets.forEach(t => t.addEventListener("scroll", onScroll, { passive: true }));
            onScroll();
            attachedTargets = targets;
        };
        trySetup();

        return () => {
            cancelled = true;
            if (setupTimerId) clearTimeout(setupTimerId);
            if (pinTimerId) clearTimeout(pinTimerId);
            if (onScroll) attachedTargets.forEach(t => t.removeEventListener("scroll", onScroll));
        };
    }, [clubId]);

    // Something outside this file resets scrollTop to 0 on every tab
    // switch - confirmed it's neither a JS `scrollTop =` write (a property
    // interceptor never caught one) nor a `.focus()` call (none fired
    // either, and preventing the tab link from ever taking focus at all -
    // see onMouseDown below - didn't stop it), so whatever's doing it
    // works below the level either of those would catch. Rather than
    // chase the exact cause further, this fights the symptom directly:
    // capture the real scroll position right before a switch (see
    // captureScrollBeforeSwitch, called from each tab's onClick) and keep
    // reasserting it for a short window right after - same "outlast
    // whatever's fighting us" approach as waitUntilTall's own reassert
    // loop above, just triggered by activeTab instead of mount.
    const preSwitchScrollRef = useRef(null);
    const tabEffectMountedRef = useRef(false);
    const captureScrollBeforeSwitch = () => {
        const hero = heroRef.current;
        const overlay = hero?.closest(".modal-overlay");
        const content = hero?.closest(".modal-content");
        const raw = Math.max(overlay?.scrollTop || 0, content?.scrollTop || 0);
        // Capped at collapseRange (the hero/header split point), not
        // carried over verbatim - past that point scrollTop is into a
        // given tab's OWN content (e.g. deep in the tournament list), which
        // means nothing on a different tab. Landing a short tab (Ratings,
        // often just one line of text) at that same raw position could
        // leave its real content scrolled up out of view entirely, above
        // whatever's on screen. Every tab can always hold at least the
        // collapse point itself (that's exactly what the min-height on the
        // tab-content wrapper guarantees), so clamping here is always safe.
        preSwitchScrollRef.current = Math.min(raw, collapseRangeRef.current);
    };

    useLayoutEffect(() => {
        // Skip the initial mount - the [clubId] effect above already owns
        // setting the opening scroll position; this is purely for
        // corrections after a switch away from that.
        if (!tabEffectMountedRef.current) {
            tabEffectMountedRef.current = true;
            return;
        }
        const target = preSwitchScrollRef.current;
        if (target == null) return;
        const hero = heroRef.current;
        const overlay = hero?.closest(".modal-overlay");
        const content = hero?.closest(".modal-content");
        const targets = [overlay, content].filter(Boolean);
        if (!targets.length) return;

        let cancelled = false;
        let tick = 0;
        // ~1.5s - see the [clubId] effect's own reassert loop for why this
        // is much longer than the click/switch itself: the new tab's async
        // data load swapping loading-skeleton content for the real content
        // a beat later is enough on its own to trigger another reset.
        const PIN_TICKS = 90;
        // setTimeout, not requestAnimationFrame - see the [clubId] effect
        // above for why (rAF pauses entirely on a backgrounded tab).
        const reassert = () => {
            if (cancelled) return;
            targets.forEach(t => { t.scrollTop = target; });
            tick += 1;
            if (tick < PIN_TICKS) setTimeout(reassert, 16);
        };
        reassert();
        return () => { cancelled = true; };
    }, [activeTab]);

    // The active tab's own search input - the compact header's search icon
    // (below) jumps straight to it, same idea as the main tournaments
    // list's "tap compact icon -> jump back to real search box". One shared
    // ref covers both searchable tabs because only ever one of them is
    // mounted at a time (see the tab-content wrapper below), so whichever
    // is on screen is the one holding it.
    const tabSearchInputRef = useRef(null);
    // Mirrors Tournament.js's own searchOpen/toggleSearch - hidden until the
    // header's search icon is clicked, instead of sitting there permanently
    // taking up space above the list.
    const [searchOpen, setSearchOpen] = useState(false);
    const toggleTabSearch = () => setSearchOpen(prev => !prev);

    // Closes itself on a tab switch - Calendar's and Players' own search
    // boxes are unrelated to each other, so leaving one open while landing
    // on the other tab would show an empty, unexplained search box there.
    useEffect(() => {
        setSearchOpen(false);
    }, [activeTab]);

    useEffect(() => {
        if (!searchOpen) return;
        // The input doesn't exist in the DOM until this same render commits
        // it (searchOpen && (...) in ClubCalendarTab/ClubPlayersTab) - defer
        // past that instead of racing it.
        const id = requestAnimationFrame(() => {
            tabSearchInputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            tabSearchInputRef.current?.focus();
        });
        return () => cancelAnimationFrame(id);
    }, [searchOpen]);

    useEffect(() => {
        if (!clubId) {
            setClub(null);
            setLoading(false);
            return;
        }
        if (clubCache[clubId]) {
            setClub(clubCache[clubId]);
            setLoading(false);
        } else {
            setLoading(true);
        }
        setActiveTab("calendar");
        api.get(`/api/v1.0/clubs/${clubId}/`)
            .then(res => {
                clubCache[clubId] = res.data;
                setClub(res.data);
            })
            .catch(() => {
                toast.error(t("Club not found"));
                handleClose();
            })
            .finally(() => setLoading(false));
    }, [clubId]);

    const handleClose = () => {
        const newQuery = new URLSearchParams(location.search);
        newQuery.delete("club_id");
        history.replace({ search: newQuery.toString() });
    };

    // Same idea as the tournament players list (TournamentPlayers.js/
    // PlayersSkeleton): don't swap in a differently-shaped placeholder block
    // and then replace it wholesale once data lands - render the real
    // .profile-hero markup right away and let individual cells fall back to
    // a <Skeleton> until `club` has a value. Same DOM the whole time, so
    // there's nothing for the browser to reflow when the data arrives - only
    // leaf content changes in place.
    if (!clubId || (!loading && !club)) return null;

    const sportName = club && t(sports.find(s => s.id === club.sport)?.name);
    // Split the 0..1 scroll range into two non-overlapping halves instead of
    // both the hero avatar and the compact header row tracking `progress`
    // directly the whole way - the hero's own avatar doesn't just shrink,
    // it also fades out over the first half, fully gone by the midpoint;
    // the compact header row (its own avatar included) only starts fading
    // in from that midpoint onward. Without this split, both were
    // simultaneously part-visible through the middle of the scroll - two
    // logos on screen together, not one handing off to the other.
    const heroAvatarOpacity = Math.max(0, 1 - progress * 2);
    const compactRowOpacity = Math.max(0, (progress - 0.5) * 2);

    return (
        <>
        <CustomModal isOpen={!!clubId} onClose={handleClose} hideCloseButton noAnimation>
            <form className="modal-form entity-profile-page">
                <div ref={heroRef} className="profile-hero">
                    {/* Title + same icons/style as .profile-header-compact-share
                        below, just in normal (non-sticky) flow at the top of the
                        hero - scrolls away with it like ordinary content, rather
                        than overlaying anything. The compact header's own copy
                        takes over once that row has scrolled in. Calendar search
                        is deliberately left out here - it only ever makes sense
                        once scrolled down to where the calendar's own search
                        input is, not by default. One flex row with
                        align-items:center, same as every other modal's own
                        title+close pairing (.modal-sticky-header h2 +
                        .modal-close) - guarantees the title and icons land on
                        the same line instead of needing separate top offsets to
                        agree. */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", paddingTop: "10px", marginBottom: "10px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 500, fontSize: "100%" }}>
                            <FeatherIcon icon="award" size={18} />
                            {t("Club")}
                        </div>
                        {/* gap:2px, not 18px - .tournament-header-right's 18px gap
                            (index.css) sits between BARE icons (no padding of their
                            own there), so it IS the edge-to-edge glyph spacing.
                            .profile-header-icon-btn (CustomModal.css) carries its own
                            8px padding on every side for a bigger tap target - that
                            padding already eats into the flex gap from both sides, so
                            matching the tournament's actual visual spacing means
                            18 - 8 - 8 = 2px of raw gap here, not 18px (which stacked
                            on top of the padding and ended up wider, not matching). */}
                        <div style={{ display: "flex", alignItems: "center", gap: "2px", flexShrink: 0 }}>
                            {(activeTab === "calendar" || activeTab === "players") && (
                                <FeatherIcon icon="search" size={16} className="profile-header-icon-btn" onClick={toggleTabSearch} />
                            )}
                            <FeatherIcon icon="share-2" size={16} className="profile-header-icon-btn" onClick={() => setIsShareOpen(true)} />
                            <FeatherIcon icon="x" size={16} className="profile-header-icon-btn" onClick={handleClose} />
                        </div>
                    </div>
                    <table style={{ width: "100%" }}>
                        <tbody>
                        <tr>
                            <td style={{ width: "110px", verticalAlign: "middle" }}>
                                {/* Shrinks and fades out over the scroll's first half (see
                                    heroAvatarOpacity above - fully gone by the midpoint, handing
                                    off to the compact header's own avatar from there) - via
                                    transform/opacity (not width/height) so none of it perturbs
                                    the table's layout, just the avatar's paint. No transition -
                                    progress already updates every real scroll frame, so one on
                                    top would just trail behind, not track, real scroll position. */}
                                {club ? (
                                    club.image
                                        ? <img
                                              src={club.image}
                                              className="thumbnail big avatar-zoomable"
                                              alt={club.name}
                                              style={{ transform: `scale(${1 - progress * 0.35})`, opacity: heroAvatarOpacity }}
                                              onClick={() => setLightboxSrc(club.image)}
                                          />
                                        : <div className="no-thumbnail big initials" style={{ transform: `scale(${1 - progress * 0.35})`, opacity: heroAvatarOpacity }}>{getInitials(club.name)}</div>
                                ) : (
                                    // Skeleton's span is inline-flex, so it rides along with
                                    // whatever ambient text-align centers it in this cell -
                                    // the real .no-thumbnail fallback is display:flex (a block
                                    // box, immune to that) and sits flush at the cell's edge.
                                    // Same wrapper here so the skeleton matches its position,
                                    // not just its size.
                                    <div style={{ display: "flex" }}>
                                        <Skeleton circle width={100} height={100} />
                                    </div>
                                )}
                            </td>
                            <td style={{ verticalAlign: "top" }}>
                                <table className="player_modal" style={{ width: "100%" }}>
                                    <tbody>
                                    <tr>
                                        <td><label>{t("Name")}</label></td>
                                        <td>{club ? club.name : <Skeleton width="90%" />}</td>
                                    </tr>
                                    <tr>
                                        <td><label>{t("Sport")}</label></td>
                                        <td>{club ? sportName : <Skeleton width="70%" />}</td>
                                    </tr>
                                    {/* Contacts/Social used to only render when the loaded club
                                        actually had that data, so the hero's own row count (and
                                        height) varied club to club - always rendering both rows
                                        keeps every club's hero the same shape, empty cell or not. */}
                                    <tr>
                                        <td><label>{t("Contacts")}</label></td>
                                        <td>
                                            {club ? (
                                                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                                                    {club.email && <a href={`mailto:${club.email}`} title={club.email}><FeatherIcon icon="mail" size={16} /></a>}
                                                    {club.phone && <a href={`tel:${club.phone}`} title={club.phone}><FeatherIcon icon="phone" size={16} /></a>}
                                                </div>
                                            ) : <Skeleton width="60%" />}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{ border: 0 }}><label>{t("Social")}</label></td>
                                        <td style={{ border: 0 }}>
                                            {club ? (
                                                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                                                    {club.instagram && <a href={club.instagram} target="_blank" rel="noreferrer"><FeatherIcon icon="instagram" size={16} /></a>}
                                                    {club.facebook && <a href={club.facebook} target="_blank" rel="noreferrer"><FeatherIcon icon="facebook" size={16} /></a>}
                                                </div>
                                            ) : <Skeleton width="30%" />}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                <div className="modal-sticky-header">
                    {/* Opacity/transform driven directly by `progress` (not the
                        .compact class's own binary opacity/transform, which has
                        its own 0.2s transition baked in - re-applying that on top
                        of an already-continuous, every-scroll-frame value would
                        make it visibly lag behind real scroll position instead of
                        tracking it), so this row fades/rises in lockstep with the
                        hero avatar shrinking above - one reads as morphing into
                        the other rather than one disappearing while the other
                        appears on its own schedule. */}
                    <div
                        className="profile-header-compact"
                        style={{
                            opacity: compactRowOpacity,
                            transform: `translateY(${(1 - compactRowOpacity) * 4}px)`,
                            pointerEvents: progress > 0.5 ? "auto" : "none",
                            transition: "none"
                        }}
                    >
                        <span className="profile-header-compact-avatar">
                            {club ? (
                                club.image
                                    ? <img src={club.image} alt="" className="avatar-zoomable" onClick={() => setLightboxSrc(club.image)} />
                                    : <span className="no-thumbnail initials">{getInitials(club.name)}</span>
                            ) : (
                                <Skeleton circle width={50} height={50} />
                            )}
                        </span>
                        <span className="profile-header-compact-titles">
                            <span className="profile-header-compact-name">{club ? club.name : <Skeleton width={90} />}</span>
                            <span className="profile-header-compact-subtitle">{club ? sportName : <Skeleton width={60} />}</span>
                        </span>
                        <span className="profile-header-compact-share" style={{ display: "flex", alignItems: "flex-start", gap: "2px" }}>
                            {(activeTab === "calendar" || activeTab === "players") && (
                                <FeatherIcon icon="search" size={16} className="profile-header-icon-btn" onClick={toggleTabSearch} />
                            )}
                            <FeatherIcon icon="share-2" size={16} className="profile-header-icon-btn" onClick={() => setIsShareOpen(true)} />
                            <FeatherIcon icon="x" size={16} className="profile-header-icon-btn" onClick={handleClose} />
                        </span>
                    </div>

                    {/* onMouseDown preventDefault: harmless good practice (stops
                        the tab link from taking focus/text-selection on click)
                        but NOT what fixes the scroll reset below - that turned
                        out not to be focus-related at all (confirmed neither a
                        `.focus()` call nor a direct `scrollTop =` write ever
                        fires around a tab switch). See
                        captureScrollBeforeSwitch/the activeTab effect above
                        for the actual fix, which just re-asserts scroll
                        position after the fact instead of relying on
                        catching whatever causes it. */}
                    <ul className="tournament-header-tabs" style={{ marginTop: "10px" }}>
                        <li className={activeTab === "calendar" ? "active" : ""}>
                            <a href="#" onMouseDown={e => e.preventDefault()} onClick={e => { e.preventDefault(); captureScrollBeforeSwitch(); setActiveTab("calendar"); }}>{t("Calendar")}</a>
                        </li>
                        <li className={activeTab === "ratings" ? "active" : ""}>
                            <a href="#" onMouseDown={e => e.preventDefault()} onClick={e => { e.preventDefault(); captureScrollBeforeSwitch(); setActiveTab("ratings"); }}>{t("Ratings")}</a>
                        </li>
                        <li className={activeTab === "players" ? "active" : ""}>
                            <a href="#" onMouseDown={e => e.preventDefault()} onClick={e => { e.preventDefault(); captureScrollBeforeSwitch(); setActiveTab("players"); }}>{t("Players")}</a>
                        </li>
                    </ul>
                </div>
                {/* Fixed min-height, not just whatever each tab's own content
                    needs - a short tab (e.g. Ratings with nothing in it yet)
                    would otherwise have too little scrollHeight to hold
                    whatever scroll position the user was already at, and the
                    browser would clamp scrollTop back down on switching to
                    it, un-collapsing the header for a tab switch alone, not
                    because the user scrolled. Keeping every tab at least
                    this tall - comfortably past any realistic viewport plus
                    the hero's own height - is what lets tab switches leave
                    scroll position (and the header/hero split) alone
                    entirely, instead of needing to actively re-pin it. */}
                <div style={{ minHeight: "100vh" }}>
                    {club && activeTab === "calendar" && <ClubCalendarTab clubId={clubId} searchInputRef={tabSearchInputRef} searchOpen={searchOpen} />}
                    {club && activeTab === "ratings" && <ClubRatingsTab clubId={clubId} />}
                    {club && activeTab === "players" && <ClubPlayersTab clubId={clubId} searchInputRef={tabSearchInputRef} searchOpen={searchOpen} />}
                </div>
            </form>
        </CustomModal>
        <TournamentShareModal
            isOpen={isShareOpen}
            onClose={() => setIsShareOpen(false)}
            url={window.location.href}
            title={club?.name}
            shareText={club?.name ? t("Check out the club {{name}}!", { name: club.name }) : undefined}
            linkLabel={t("Club link")}
            qrCaption={t("Scan with camera to open the club")}
        />
        <PhotoLightbox src={lightboxSrc} alt={club?.name} onClose={() => setLightboxSrc(null)} />
        </>
    );
};

export default ClubProfileModal;
